/**
 * EL FORMULARIO DEL REPORTE: el quincenal y el mensual, que ya no son el mismo.
 *
 * Hasta el doc del 16-08 había UN formulario y lo único que cambiaba entre uno y otro
 * eran las medidas. Son dos cuestionarios distintos (T7 y T8) y el mensual además no es
 * igual para los tres planes, así que aquí solo vive lo común: traer lo que la app ya
 * sabe, guardar lo que el cliente contesta, revisarlo antes de mandarlo y decirle qué
 * pasa ahora.
 *
 * Qué bloques lleva cada uno NO se decide aquí: lo dice el servidor en
 * `GET /reports/formulario`, leyendo las habilitaciones de su plan. El plan es un dato,
 * nunca un nombre.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import ReporteQuincenal from './ReporteQuincenal';
import { MensualTuMes, MensualTuPeso } from './MensualTuMes';
import MensualSeccion from './MensualSeccion';
import MensualPaso3 from './MensualPaso3';
import MensualPaso4 from './MensualPaso4';
import QuincenalPaso1 from './QuincenalPaso1';
import QuincenalResumen from './QuincenalResumen';
import QuincenalPreguntas, { QuincenalEnviado } from './QuincenalPreguntas';
import { BarraDeTramos, CabeceraDelQuincenal } from './piezas';
import useBorradorEnElServidor from '../../hooks/useBorradorEnElServidor';
import QuincenalPaso3, { pasosDelRapido } from './QuincenalPaso3';
import { CabeceraDePasos, RotuloDelPaso, ROTULOS_DEL_QUINCENAL } from './PasosDelMensual';
import { MEDIDAS, revisarMedidas } from '../../lib/medidas';
import {
    faltaEnElQuincenal, faltaEnElReporteCorto, faltaEnLaSeccion,
    respuestasDelMensualParaElServidor, respuestasDelQuincenalParaElServidor,
} from '../../lib/revisarReporte';
import { hoyLocal } from '../../lib/horaEspana';
import { borrarBorrador, claveDelBorrador, guardarBorrador, hayAlgoQueGuardar, leerBorrador } from '../../lib/borradorDelReporte';
import { objetivoVisible } from '../../lib/objetivos';
import { revisarPeso } from '../../lib/pesoValido';
import { useConfirm } from '../ui/confirm';
import { mensajeDeError } from '../../lib/mensajeDeError';

const ORANGE = '#FF671F';

// Si el informe ya lo puede ver al enviar: con un plan personalizado el servidor contesta
// «pendiente_revision» hasta que el entrenador lo publica, y entonces no se ofrece.
const informeQueYaVe = (informe) => informe?.motivo !== 'pendiente_revision';

// El título por cadencia (tarea 1.6). Antes era un ternario binario: todo lo que no era
// mensual se rotulaba «Tu reporte quincenal», y al de cadencia semanal el aviso le decía
// «semanal» y el formulario «quincenal». El tipo lo manda el servidor; si llega uno que
// no está aquí, se dice «Tu reporte» a secas antes que ponerle una cadencia inventada.
const TITULOS_REPORTE = {
    semanal: 'Tu reporte semanal',
    quincenal: 'Tu reporte quincenal',
    mensual: 'Tu reporte mensual',
};

// El de la CABECERA DE LOS PASOS, sin el «Tu» (doc 1-09: «Reporte quincenal», igual que
// «Reporte mensual»). Ahí el título va en versalitas y grande, y el posesivo delante de una
// palabra en mayúsculas se lee como un error de maquetación, no como cercanía.
const TITULOS_EN_LA_CABECERA = {
    semanal: 'Reporte semanal',
    quincenal: 'Reporte quincenal',
    mensual: 'Reporte mensual',
};

const valoresIniciales = (objetivoActual) => ({
    weight: '',
    // El día de ese peso si no es el de hoy (mensual del 28-10: el del check-in de miércoles a
    // viernes). Viaja con el envío para que el servidor no lo apunte otra vez con el de hoy.
    peso_fecha: '',
    measurements: Object.fromEntries(MEDIDAS.map(m => [m.key, ''])),
    // Quincenal
    entreno_huecos: '',
    molestias: '',
    sensaciones: null,
    // Las cuatro de sí o no del quincenal del 24-10: {clave: {respuesta, detalle}}. Vacío es
    // «no» en todas, que es como salen marcadas (`lib/revisarReporte`).
    respuestas_quincenal: {},
    // Las del mensual del 25-10, por secciones: {clave: valor} (`lib/revisarReporte`). Y en
    // «Tu material», las especiales que sí tiene y qué listas ha confirmado.
    respuestas_mensual: {},
    maquinas_especiales: [],
    // Las dos escalas de 0 a 10 del paso 2 (doc 1-09). No sustituyen a `sensaciones`, que
    // son las cinco estrellas de los reportes de antes: son otra pregunta y otra escala.
    sensaciones_0a10: null,
    esfuerzo_resultados: null,
    // Y las cinco estrellas del paso 1 cuando no hay check-in con los que llenarlo.
    dieta_grado: null,
    entreno_grado: null,
    cardio_grado: null,
    suplementacion_grado: null,
    descanso_grado: null,
    // Semanal (doc 21-08): qué le altera la rutina la semana que viene.
    semana_proxima: '',
    // Los huecos del paso 1 del reporte corto (`QuincenalPaso1`).
    huecos_paso1: {},
    dieta_dificultad: '',
    viabilidad_ajuste: '',
    entreno: { confirmacion: {}, estrellas: null, nota: '', regularidad: '',
               rutina_del_mes: '', quiere_saber_del_silver: null },
    // `lesiones` se queda en el estado aunque ya no se pregunte: los reportes viejos se
    // leen con ella. Lo que se contesta desde el 3-09 es `ejercicios_molestos`.
    lesiones: [],
    lesion_nueva_hay: '',
    lesion_nueva: '',
    ejercicios_molestos: [],
    cardio_proximo_mes: '',
    suplementacion: { respuesta: '', detalle: '' },
    // El motivo de no haberla tomado, que solo se pregunta si dejó días (doc 1-09).
    suplementacion_motivo: '',
    energia_motivo: '',
    valoracion_resultado: null,
    motivacion: null,
    // Las del documento del 1-09: el compromiso (habla de él), las expectativas de 0 a 10
    // y las máquinas que no tiene. `objetivo_cambio` no viaja: solo decide si se le
    // pregunta cuál es el nuevo objetivo.
    compromiso: '',
    expectativas: null,
    maquinas_no_disponibles: [],
    objetivo_cambio: '',
    proximo_objetivo: '',
    notes: '',
    // Con qué objetivo venía: se le enseña encima de «¿Quieres cambiar de objetivo?».
    objetivo_actual: objetivoActual || '',
});

const FormularioReporte = ({ api, token, tipoRevision, windowState, prev, perfilCliente,
                            onEnviado, onAplazado, onVerInforme, onVerHistorico }) => {
    const { confirm } = useConfirm();
    const [cargando, setCargando] = useState(true);
    const [ficha, setFicha] = useState(null);          // lo que manda el servidor
    // El objetivo que se le enseña en el mensual es el ACTUAL de la ficha (lo pone el
    // entrenador, doc de Jesús del 2-09, fase 2), y si la ficha no lo trae todavía, el
    // `goal` viejo traducido a la lista nueva. Antes era el `goal` a pelo.
    const [valores, setValores] = useState(() => valoresIniciales(objetivoVisible(perfilCliente)));
    const [paso, setPaso] = useState('form');          // form | enviado
    // LOS DOS VAN POR PASOS, y son los mismos primeros dos.
    //
    //   MENSUAL (doc «El reporte mensual», 1-09): 1 sus datos, 2 sus respuestas, 3 fotos y
    //   medidas, y el 4 es la entrega, que solo se ve después de mandarlo.
    //   QUINCENAL («Todo lo validado antes del 1 de septiembre»): 1 sus datos, 2 sus
    //   sensaciones y dudas, y el 3 es la respuesta, también después de mandarlo.
    //
    // Un solo contador para los dos: son la misma máquina y lo único que cambia es cuántos
    // pasos hay y qué se pinta en cada uno.
    const [pasoMensual, setPasoMensual] = useState(1);
    // El informe, si de verdad está publicado, para el paso 4. Sin esto la pantalla le
    // diría «ya lo tienes» sin nada que abrir.
    const [informeListo, setInformeListo] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [aplazado, setAplazado] = useState(false);
    // Mientras viaja la petición de aplazar: el botón se apaga y una segunda pulsación no
    // sale (12-10: dos pulsaciones contaban dos aplazamientos). El estado apaga el botón;
    // la referencia frena la pulsación que llega antes de que React vuelva a pintar.
    const [aplazando, setAplazando] = useState(false);
    const aplazandoRef = useRef(false);
    // El peso que ya confirmó cuando se le preguntó por un salto raro: no se le pregunta lo
    // mismo otra vez en cada paso (12-10: salía tres veces).
    const pesoConfirmado = useRef(null);
    // El aplazamiento en tres pasos (Jesús, 20-10): «Necesito aplazarlo una semana más»
    // pregunta «¿Estás seguro?» (`'seguro'`) y, con «Sí, aplázamelo», pide el motivo
    // (`'motivo'`). Con ?aplazar=1 llega ya en la pregunta: es el camino desde el botón
    // del aviso, que ya dijo que no puede.
    const [pidiendoAplazar, setPidiendoAplazar] = useState(() =>
        new URLSearchParams(window.location.search).get('aplazar') === '1' ? 'seguro' : null);
    const [notaAplazar, setNotaAplazar] = useState('');

    const set = (campo, valor) => setValores(v => ({ ...v, [campo]: valor }));

    // LO ESCRITO NO SE PIERDE AL SALIR (12-10). "Atrás" del navegador a mitad del mensual
    // sacaba a Inicio y al volver se empezaba de cero. Del paso 2 en adelante se va
    // guardando un borrador en la pestaña (`lib/borradorDelReporte`) y al volver a abrir el
    // formulario se recupera, con el paso en el que estaba. Se borra al enviar y al aplazar.
    // En modo revisión no se guarda nada: ahí no se manda ningún reporte.
    const claveBorrador = (tipo) => (tipoRevision ? null
        : claveDelBorrador(perfilCliente?.id, tipo, windowState?.closes_at));
    const borradorMirado = useRef(false);

    const cargar = useCallback(async () => {
        try {
            const r = await api.get('/reports/formulario', {
                params: tipoRevision ? { tipo: tipoRevision } : {},
            });
            setFicha(r.data);
            setAplazado(!!r.data?.aplazado_hasta);
            // Las lesiones abiertas entran ya en el estado: lo que se contesta es su
            // estado de este mes, no volver a escribirlas.
            const abiertas = (r.data?.datos?.lesiones || []).map(l => ({
                zona: l.zona, desde: l.desde, estado_mes: '',
                ejercicios: l.ejercicios_vetados || [],
            }));
            // EL PESO, YA PUESTO, PARA PODER CONFIRMARLO (paso 1 del doc del 1-09:
            // «Actualizar tus datos y confirmar que están bien»). El peso de la semana ya
            // lo calcula el servidor; si el cliente no lo toca, confirma ese.
            //
            // Menos en la rama «ultimo», donde la casilla se deja vacía A PROPÓSITO (punto
            // 34 del doc del 24-08): ese peso no es de esta semana, así que rellenarlo
            // sería darle por bueno un número viejo sin que nadie lo mire.
            //
            // CON PUNTO, NO CON COMA. La casilla es un `type="number"` y un navegador tira
            // el valor entero si no es un número que él entienda: con «78,6» dentro, la
            // casilla salía VACÍA. O sea que al darle a «Modificar» para corregir el peso,
            // se abría en blanco en vez de traer el número que estaba a punto de confirmar.
            // Confirmar sí funcionaba -- `revisarElPeso` acepta las dos --, pero el cliente
            // no veía qué estaba tocando. La coma es para LEER; dentro de una casilla de
            // número va el punto.
            // EN EL QUINCENAL DEL 24-10 Y EN EL MENSUAL DEL 25-10, NADA: su peso es el de hoy
            // y lo pone el resumen (`QuincenalResumen`, `MensualResumen`) solo si es de hoy;
            // si no, se le pide.
            const conResumen = Array.isArray(r.data?.preguntas_quincenal)
                || Array.isArray(r.data?.secciones_mensual);
            const ps = conResumen ? null : r.data?.datos?.peso_semanal;
            const puesto = ps && ps.regla !== 'ultimo' && ps.valor != null
                ? String(ps.valor) : '';
            // Y las máquinas que no tiene, con lo que dejó la última vez: el documento dice
            // «actualiza aquí tu listado», y para eso el listado tiene que estar.
            const maquinas = r.data?.datos?.maquinas || [];
            const especiales = r.data?.datos?.maquinas_especiales || [];
            // Y los ejercicios que le molestan, por lo mismo: «estos son los que me diste,
            // quita los que ya no». Vienen de `injuries`, con lo que dejó dentro de sus
            // lesiones abiertas sumado, para que la primera vez no le salga en blanco.
            const molestias = r.data?.datos?.ejercicios_molestos || [];
            setValores(v => ({ ...v, lesiones: abiertas, weight: v.weight || puesto,
                               maquinas_no_disponibles: maquinas,
                               maquinas_especiales: especiales,
                               ejercicios_molestos: molestias }));
            // Y encima, lo que llevaba escrito si salió a medias (ver arriba). PRIMERO EL DEL
            // SERVIDOR (28-10: «por si cierra sesión y vuelve a abrirlo»), y si no hay, el de
            // la pestaña. El de otro día vuelve sin el peso, como el de la pestaña.
            let borrador = null;
            if (claveBorrador(r.data?.tipo)) {
                try {
                    const b = await api.get('/reports/borrador',
                        { params: { tipo: r.data.tipo, cierre: windowState.closes_at } });
                    const enServidor = b.data?.borrador;
                    if (enServidor?.valores && Object.keys(enServidor.valores).length) {
                        const v = { ...enServidor.valores };
                        if (enServidor.dia && enServidor.dia !== hoyLocal()) { delete v.weight; delete v.peso_fecha; }
                        borrador = { valores: v, paso: enServidor.paso || 1 };
                    }
                } catch (e) {
                    console.error('No se pudo leer el borrador del reporte', e);
                }
            }
            borrador = borrador || leerBorrador(claveBorrador(r.data?.tipo));
            if (borrador) {
                setValores(v => ({ ...v, ...borrador.valores }));
                setPasoMensual(borrador.paso);
                // Con `id`: la carga puede correr dos veces y el aviso salía repetido (14-10).
                toast.info('Hemos recuperado lo que llevabas escrito.', { id: 'borrador-recuperado', siempre: true });
            }
            borradorMirado.current = true;
        } catch (e) {
            console.error('No se pudo cargar el formulario del reporte', e);
            toast.error('No hemos podido preparar tu reporte. Inténtalo en un momento.');
        } finally {
            setCargando(false);
        }
    }, [api, tipoRevision]);

    useEffect(() => { cargar(); }, [cargar]);

    // Guardar el borrador: solo con el formulario ya cargado (si no, se pisaría el guardado
    // con el formulario en blanco) y solo con algo escrito: del paso 2 en adelante, o en el
    // paso 1 cuando ya ha contestado alguna de sus preguntas (`hayAlgoQueGuardar`, 14-10).
    // Y EN EL SERVIDOR (28-10), con el mismo guardado en fila del check-in: al rato de cada
    // cambio y, si sale de la pantalla con algo sin mandar, en el acto.
    const borradorEnServidor = useBorradorEnElServidor(api, '/reports/borrador', () => ({
        tipo: ficha?.tipo, cierre: windowState?.closes_at,
        valores, paso: pasoMensual, dia: hoyLocal(),
    }), { espera: 1200 });
    useEffect(() => {
        if (!borradorMirado.current || !ficha || paso === 'enviado' || aplazado) return;
        if (!hayAlgoQueGuardar(valores, pasoMensual)) return;
        guardarBorrador(claveBorrador(ficha.tipo), valores, pasoMensual);
        if (claveBorrador(ficha.tipo)) borradorEnServidor.programar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valores, pasoMensual, paso, ficha, aplazado]);

    // «El rápido, mensual» (doc 19-08): en la Calculadora (y ELM) el mensual va con el
    // formulario corto. La cadencia la decide el servidor; aquí solo se elige pantalla.
    const esMensual = ficha?.tipo === 'mensual' && ficha?.forma !== 'rapido';
    // LAS SECCIONES DEL MENSUAL DEL 25-10, del servidor (`core/mensual`): una pantalla cada una.
    const secciones = ficha?.secciones_mensual || [];
    const seccion = esMensual ? secciones[pasoMensual - 1] || secciones[0] : null;
    const material = secciones.find((x) => x.clave === 'material');

    // EL RESUMEN DEL MENSUAL (sección 1), con SU día: de él sale cuántos días cuenta. Lo
    // necesitan el resumen y las secciones de después (las estrellas de sus check-in).
    const [resumen, setResumen] = useState(null);
    const [cargandoResumen, setCargandoResumen] = useState(false);
    const [falloResumen, setFalloResumen] = useState(false);
    const cargarResumen = useCallback(async () => {
        setCargandoResumen(true);
        try {
            const r = await api.get('/reports/mensual/resumen', { params: { hoy: hoyLocal() } });
            setResumen(r.data);
            setFalloResumen(false);
        } catch (e) {
            console.error('No se pudo cargar el resumen del mensual', e);
            setFalloResumen(true);
        } finally {
            setCargandoResumen(false);
        }
    }, [api]);
    useEffect(() => { if (esMensual) cargarResumen(); }, [esMensual, cargarResumen]);
    const grados = resumen?.grados || {};

    // «TU PESO» SE SALTA si ya se pesó en el check-in el miércoles, jueves o viernes (28-10):
    // ese es el peso del reporte y se empieza en «Tu mes». Sin él, y sin peso en lo que traía
    // el borrador, se vuelve a «Tu peso». Una vez, cuando llegan el resumen y el borrador.
    //
    // REGLA DEL 29-10 (Enrique): el servidor solo manda `peso_de_la_semana` si HOY ya hay peso
    // apuntado, y es el PESO DE REFERENCIA SEMANAL. Ese es el peso del reporte, también cuando
    // llega después de pesarse en «Tu peso» (se vuelve a pedir el resumen): manda sobre el
    // de hoy y sobre el del borrador. Ya confirmado: es un peso suyo de esta semana.
    const pesoDeLaSemana = resumen?.peso_de_la_semana || null;
    const pesoResuelto = useRef(false);
    useEffect(() => {
        if (!esMensual || !resumen || cargando) return;
        if (pesoDeLaSemana) {
            setValores((v) => ({ ...v, weight: String(pesoDeLaSemana.valor), peso_fecha: pesoDeLaSemana.fecha }));
            pesoConfirmado.current = Number(pesoDeLaSemana.valor);
        }
        if (pesoResuelto.current) return;
        pesoResuelto.current = true;
        if (pesoDeLaSemana) {
            setPasoMensual((n) => Math.max(n, 2));
        } else {
            setPasoMensual(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esMensual, resumen, cargando]);

    // LAS SECCIONES SIN «CONTINUAR» (`automatica`, 28-10) pasan solas al contestarlas. Si vuelve
    // atrás a una que ya estaba entera, sí lleva el botón: si no, no podría salir sin cambiar
    // nada. Se mira al entrar en cada sección.
    const [enteraAlEntrar, setEnteraAlEntrar] = useState(false);
    useEffect(() => {
        if (seccion) setEnteraAlEntrar(!faltaEnLaSeccion(seccion, valores, grados));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pasoMensual, seccion?.clave]);

    // EL QUINCENAL DEL DOC DEL 24-10: resumen y cinco preguntas. Solo el quincenal; el
    // semanal y el mensual rápido siguen con el formulario corto de siempre. Lo dice la ficha
    // con sus preguntas (`preguntas_quincenal`, de `core/quincenal`).
    const preguntasQuincenal = ficha?.preguntas_quincenal;
    const esQuincenal = ficha?.tipo === 'quincenal' && Array.isArray(preguntasQuincenal);
    const bloques = ficha?.bloques || [];

    // El plazo, en hora de España y con lo que queda. Lo calcula quien nos llama
    // (la pantalla ya lo tenía resuelto) y aquí solo se pinta, en el aviso del paso 3. La
    // cabecera ya no lo lleva, ni la semana (Jesús, 20-10: «eliminarlo directamente»).
    const plazo = {
        // `cierre` ya empieza por «hasta»; `cuando` es la misma fecha sin él, para las
        // frases que la meten dentro (el aviso del paso 3).
        cierre: windowState?.plazoLabel || null,
        cuando: windowState?.cierreLabel || null,
        queda: windowState?.quedaLabel || null,
    };

    const aplazar = async (nota) => {
        if (aplazandoRef.current) return;
        aplazandoRef.current = true;
        setAplazando(true);
        try {
            const r = await api.post('/reports/aplazar', nota ? { nota } : {});
            setAplazado(true);
            setPidiendoAplazar(null);
            borradorEnServidor.cancelar();
            borrarBorrador(claveBorrador(ficha?.tipo));
            toast.success(r.data?.titulo || 'Te lo he aplazado 7 días', {
                description: r.data?.mensaje, duration: 8000,
            });
            // Y LA PANTALLA CAMBIA SOLA (12-10): seguía con el formulario abierto y dejaba
            // rellenarlo entero para contestar al final que todavía no tocaba. Quien nos
            // pinta vuelve a pedir la ventana, que ya viene cerrada hasta que se reabra.
            if (onAplazado) onAplazado();
        } catch (e) {
            console.error('No se pudo aplazar el reporte', e);
            toast.error(mensajeDeError(e, 'No hemos podido aplazártelo. Inténtalo en un momento.'));
        } finally {
            aplazandoRef.current = false;
            setAplazando(false);
        }
    };

    /** El peso: obligatorio, dentro de rango y sin saltos raros sin confirmar. */
    const revisarElPeso = async () => {
        if (!valores.weight) { toast.error('El peso es obligatorio'); return false; }
        // CONTRA EL PESO QUE TIENE LA APP, no solo contra el del reporte anterior (12-10):
        // en su primer reporte no hay anterior, y con 82 kg apuntados ese mismo día en el
        // alta aceptaba 41 sin preguntar. Si la app no tiene peso suyo, el del último reporte.
        const chequeo = revisarPeso(valores.weight, perfilCliente?.weight ?? prev?.weight);
        if (!chequeo.ok) { toast.error(chequeo.error); return false; }
        // Lo que ya confirmó no se le vuelve a preguntar mientras no cambie el número.
        if (chequeo.confirmar && pesoConfirmado.current !== chequeo.peso) {
            if (!await confirm({
                title: 'Confírmame el peso',
                description: chequeo.confirmar,
                confirmLabel: 'Sí, es correcto', cancelLabel: 'Lo corrijo',
            })) return false;
            pesoConfirmado.current = chequeo.peso;
        }
        return true;
    };

    /** Las diez medidas, y van todas: media tabla no se puede comparar con el mes pasado. */
    const revisarLasMedidas = () => {
        // La regla (que estén las diez y que sean medidas) vive en `lib/medidas`.
        const chequeo = revisarMedidas(valores.measurements);
        if (chequeo.ok) return true;
        toast.error(chequeo.error);
        return false;
    };

    /** Lo que le falta a una sección del mensual para pasar a la siguiente. */
    const revisarLaSeccion = (s) => {
        const falta = faltaEnLaSeccion(s, valores, grados);
        if (falta) { toast.error(falta); return false; }
        return true;
    };

    /** Lo que no se puede mandar a medias. Devuelve true si se puede seguir. */
    const revisar = async () => {
        if (!await revisarElPeso()) return false;
        // Todas sus secciones: también aquí, por si vuelve de un borrador en la última.
        if (esMensual) return secciones.every(revisarLaSeccion) && revisarLasMedidas();
        // El reporte corto tampoco se manda vacío: sus dos escalas (`lib/revisarReporte`). El
        // quincenal ya no las lleva: ahí lo que no puede ir es un «sí» sin decir cuál.
        const falta = esQuincenal ? faltaEnElQuincenal(valores, preguntasQuincenal)
            : faltaEnElReporteCorto(valores);
        if (falta) { toast.error(falta); return false; }
        return true;
    };

    const enviar = async () => {
        if (!await revisar()) return;

        // EN MODO REVISIÓN NO SE MANDA NADA, PERO EL ÚLTIMO PASO SE VE.
        //
        // `?ver=` existe para que el equipo mire un reporte fuera de su semana, y el paso
        // 4 -- la entrega, que es donde se le da algo al cliente -- era el único que no se
        // podía mirar: solo aparece después de un envío, y el servidor rechaza el envío
        // fuera de ventana («Todavía no toca: se abre el viernes...»). O sea que la
        // pantalla que más falta hace repasar era justo la que no se podía abrir.
        //
        // Aquí no se escribe nada: no hay POST, no se crea reporte, no se toca su ficha.
        // Se pinta el paso 4 tal cual, con el informe de su último reporte para que el
        // botón lleve a algún sitio.
        if (tipoRevision) {
            try {
                const r = await api.get('/reports');
                const ultimo = (r.data || [])[0];
                if (ultimo?.id) {
                    const inf = await api.get(`/reports/${ultimo.id}/informe`);
                    setInformeListo(informeQueYaVe(inf.data) ? ultimo.id : null);
                }
            } catch { setInformeListo(null); }
            setPaso('enviado');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            toast.info('Modo revisión: no se ha enviado nada. Esto es el paso 4 tal y como lo ve el cliente.');
            return;
        }

        setEnviando(true);
        // Lo que iba al borrador ya va en el envío: que no llegue detrás y lo vuelva a crear.
        borradorEnServidor.cancelar();
        try {
            const payload = {
                tipo: ficha?.tipo,
                weight: parseFloat(String(valores.weight).replace(',', '.')),
                peso_fecha: esMensual && valores.peso_fecha && valores.peso_fecha !== hoyLocal()
                    ? valores.peso_fecha : null,
                hoy: hoyLocal(),
                // Sin `body_fat`: el reporte ya no pide el % de grasa (Jesús, 20-10). El
                // servidor lo sigue aceptando y, sin él, no toca su serie.
                measurements: Object.fromEntries(
                    Object.entries(valores.measurements).filter(([, v]) => v)
                        .map(([k, v]) => [k, parseFloat(String(v).replace(',', '.'))])),
                // El cumplimiento del entreno lo sigue calculando el servidor con esto:
                // la diferencia entre no entrenar y entrenar sin apuntarlo.
                huecos: huecosParaElServidor(ficha, valores),
                molestias: valores.molestias || null,
                sensaciones: valores.sensaciones,
                // Solo la rellena el semanal; en el resto ni se pregunta y viaja a null.
                semana_proxima: valores.semana_proxima || null,
                dieta_dificultad: valores.dieta_dificultad || null,
                viabilidad_ajuste: valores.viabilidad_ajuste || null,
                entreno: null,
                lesiones: !esMensual && bloques.includes('lesiones')
                    ? valores.lesiones.filter(l => l.zona).map(l => ({
                        zona: l.zona, desde: l.desde || null,
                        estado_mes: l.estado_mes || null, ejercicios: l.ejercicios || [] }))
                    : null,
                lesion_nueva: valores.lesion_nueva_hay === 'si' ? (valores.lesion_nueva || null) : null,
                // La pregunta 5 del doc del 1-09. Va con el mismo candado que las lesiones:
                // solo la contesta quien lleva ese bloque en su plan.
                // En el mensual del 25-10, los de «Tu material», que solo salen con rutina.
                ejercicios_molestos: (esMensual ? material?.ejercicios : bloques.includes('lesiones'))
                    ? (valores.ejercicios_molestos || []) : null,
                cardio_proximo_mes: valores.cardio_proximo_mes || null,
                suplementacion: valores.suplementacion.respuesta
                    ? { respuesta: valores.suplementacion.respuesta,
                        detalle: valores.suplementacion.detalle || null }
                    : null,
                suplementacion_motivo: valores.suplementacion_motivo || null,
                energia_motivo: valores.energia_motivo || null,
                valoracion_resultado: valores.valoracion_resultado,
                motivacion: valores.motivacion,
                // Las tres del documento del 1-09. Las máquinas viajan aunque la lista esté
                // vacía: vaciarla es una respuesta («ya no me falta ninguna») y mandarla a
                // null dejaría en el perfil las del mes pasado.
                compromiso: valores.compromiso || null,
                expectativas: valores.expectativas,
                maquinas_no_disponibles: esMensual ? (valores.maquinas_no_disponibles || []) : null,
                maquinas_especiales: esMensual ? (valores.maquinas_especiales || []) : null,
                proximo_objetivo: valores.proximo_objetivo || null,
                notes: valores.notes || null,
                // Las del quincenal de tres pasos (doc 1-09): las dos escalas del paso 2 y,
                // sólo cuando no había check-in que enseñar, las cinco del paso 1.
                sensaciones_0a10: valores.sensaciones_0a10,
                esfuerzo_resultados: valores.esfuerzo_resultados,
                dieta_grado: valores.dieta_grado,
                entreno_grado: valores.entreno_grado,
                cardio_grado: valores.cardio_grado,
                suplementacion_grado: valores.suplementacion_grado,
                descanso_grado: valores.descanso_grado,
                // Las cuatro de sí o no del quincenal del 24-10 (la quinta va en `notes`).
                respuestas_quincenal: esQuincenal
                    ? respuestasDelQuincenalParaElServidor(valores, preguntasQuincenal) : null,
                // Las de las secciones del mensual del 25-10 (`core/mensual`).
                respuestas_mensual: esMensual
                    ? respuestasDelMensualParaElServidor(valores, secciones, grados) : null,
            };
            const r = await api.post('/reports', payload);
            borrarBorrador(claveBorrador(ficha?.tipo));
            // EL MENSUAL, AL ENVIARLO (Enrique, 29-10): TODOS, con entrenador o sin él, al
            // «Histórico de informes» con el informe de este reporte abierto. El servidor lo
            // genera al recibirlo y ya no se lo esconde a nadie; el feedback llega después, con
            // su aviso. Aquí no hay campanita: solo el mensaje.
            if (esMensual && onVerHistorico) {
                // La frase la manda el servidor (`ficha.frase_al_enviar`): «Nos pondremos en
                // contacto contigo» solo a quien le contesta alguien.
                toast.success(ficha?.frase_al_enviar || 'Hemos recibido tu reporte mensual.',
                    { duration: 8000, siempre: true });
                onVerHistorico(r.data?.id || null);
                return;
            }
            // El `mensaje_envio` del servidor ya no se pinta: los dos últimos pasos -- el 3
            // del quincenal y el 4 del mensual -- dicen lo mismo con más detalle.
            // ¿Le sale ya su informe? El paso 4 se lo ofrece SOLO si de verdad puede
            // abrirlo: mientras esté pendiente de revisión, el servidor se lo niega (T9),
            // y una tarjeta que dice «ya lo tienes» sin nada detrás es peor que no ponerla.
            let informeAbrible = null;
            if (esMensual && r.data?.id) {
                try {
                    const inf = await api.get(`/reports/${r.data.id}/informe`);
                    informeAbrible = informeQueYaVe(inf.data) ? r.data.id : null;
                    setInformeListo(informeAbrible);
                } catch { setInformeListo(null); }
            }
            setPaso('enviado');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (onEnviado) onEnviado();
        } catch (error) {
            console.error('Error al enviar el reporte', error);
            toast.error(mensajeDeError(error, 'No hemos podido enviar tu reporte. Inténtalo en un momento.'));
            // No llegó: lo escrito, al borrador del servidor, que no se pierda.
            if (claveBorrador(ficha?.tipo)) borradorEnServidor.guardarYa().catch(() => {});
        } finally {
            setEnviando(false);
        }
    };

    if (cargando) {
        return (
            <div className="animate-pulse space-y-3">
                <div className="h-6 bg-muted rounded w-1/2" />
                <div className="h-40 bg-card rounded-2xl" />
                <div className="h-40 bg-card rounded-2xl" />
            </div>
        );
    }
    if (!ficha) return null;

    // ── Enviado. Lo que pasa ahora (sin día desde el 18-10: «no hagas promesas») ──
    //
    // En el mensual eso ya no es una tarjeta: es el PASO 4, «Entregarte el plan nuevo con
    // el informe y darte feedback», que es el paso que justifica los otros tres.
    if (paso === 'enviado') {
        if (esMensual) {
            return (
                <MensualPaso4
                    api={api} token={token}
                    informeId={informeListo} onVerInforme={onVerInforme}
                    conRespuesta={ficha?.con_respuesta !== false}
                    fraseAlEnviar={ficha?.frase_al_enviar} />
            );
        }
        // Y en el quincenal, el PASO 3: «Este tercer paso es cosa nuestra». Era una tarjeta
        // de «Reporte enviado» con una línea; ahora es el paso que sale anunciado desde la
        // primera pantalla.
        if (esQuincenal) {
            return <QuincenalEnviado fraseAlEnviar={ficha?.frase_al_enviar} />;
        }
        return (
            <QuincenalPaso3
                titulo={TITULOS_EN_LA_CABECERA[ficha?.tipo] || 'Reporte'}
                conRespuesta={ficha?.con_respuesta !== false}
                fraseAlEnviar={esQuincenal ? ficha?.frase_al_enviar : null} />
        );
    }

    /** Del paso 1 al 2: lo que se confirma ahí es el peso, así que se revisa ahí. */
    const confirmarPaso1 = async () => {
        if (!await revisarElPeso()) return;
        setPasoMensual(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const irAPaso = (n) => { setPasoMensual(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    // EL APLAZAMIENTO SALE UNA VEZ, no en cada paso: es una decisión sobre el reporte
    // entero, y repetirla tres veces la convierte en una salida a mano. Va debajo de
    // «Continuar» del paso 1: «que la opción sea necesito continuar o necesito aplazar una
    // semana más» (Jesús, 20-10).
    //
    // La llamada es la de siempre (`aplazar`, POST /reports/aplazar): un aplazamiento por
    // ventana y la escritura condicionada en el servidor, así que pulsar dos veces o
    // reintentar no cuenta dos. El motivo viaja como `nota`, que es lo que su entrenador ve
    // junto al aplazamiento en el panel.
    const motivoAplazar = notaAplazar.trim();
    const bloqueAplazar = (
        <>
            {aplazado ? (
                <div data-testid="aplazar"
                    className="w-full rounded-2xl p-4 border text-sm border-green-500/40 bg-green-500/5 text-foreground">
                    {/* Sin día de la semana: el quincenal cierra en jueves y el mensual
                        en lunes, y esto sale en los dos (P44 del 23-08). Y en plural. */}
                    Te lo hemos aplazado 7 días: tu reporte se vuelve a abrir la semana que viene, el mismo día de siempre. Sigue registrando como hasta ahora.
                </div>
            ) : !pidiendoAplazar ? (
                <button type="button" onClick={() => setPidiendoAplazar('seguro')} data-testid="aplazar"
                    className="w-full text-center rounded-2xl p-3 border text-sm transition-colors border-border bg-card text-foreground/70 hover:border-foreground/30">
                    Necesito aplazarlo una semana más
                </button>
            ) : pidiendoAplazar === 'seguro' ? (
                <div className="w-full rounded-2xl p-4 border border-border bg-card space-y-3" data-testid="aplazar-seguro">
                    {/* El texto del documento de Jesús. En el vídeo dice «que recibirás»;
                        vale el escrito, «no recibirás». */}
                    <p className="text-sm text-foreground">
                        ¿Estás seguro? Aplazarlo implica que no recibirás tus ajustes esta semana.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button type="button" data-testid="aplazar-si"
                            onClick={() => setPidiendoAplazar('motivo')}
                            className="flex-1 rounded-xl py-2.5 text-sm font-bold border border-border text-foreground hover:border-foreground/30">
                            Sí, aplázamelo
                        </button>
                        <button type="button" data-testid="aplazar-no"
                            onClick={() => setPidiendoAplazar(null)}
                            className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white"
                            style={{ backgroundColor: ORANGE }}>
                            No, te lo mando ahora
                        </button>
                    </div>
                </div>
            ) : (
                <div className="w-full rounded-2xl p-4 border border-border bg-card space-y-3" data-testid="aplazar-confirmar">
                    <label htmlFor="aplazar-nota" className="block text-sm text-foreground">Indica el motivo</label>
                    <textarea id="aplazar-nota" value={notaAplazar} onChange={(e) => setNotaAplazar(e.target.value)}
                        rows={3} maxLength={500} placeholder="Estoy de viaje hasta el jueves..."
                        data-testid="aplazar-nota"
                        className="w-full bg-muted border border-input rounded-xl px-3 py-2 text-sm text-foreground placeholder-foreground/30 focus:outline-none focus:border-[#FF671F] resize-none" />
                    <div className="flex gap-2">
                        <button type="button" data-testid="aplazar-enviar"
                            disabled={aplazando || !motivoAplazar}
                            onClick={() => aplazar(motivoAplazar)}
                            className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-40"
                            style={{ backgroundColor: ORANGE }}>
                            {aplazando ? 'Aplazando...' : 'Aplazar una semana'}
                        </button>
                        <button type="button" onClick={() => setPidiendoAplazar(null)} disabled={aplazando}
                            data-testid="aplazar-volver"
                            className="rounded-xl px-4 py-2.5 text-sm text-foreground/70 border border-border hover:border-foreground/30">
                            Mejor te lo mando
                        </button>
                    </div>
                </div>
            )}
        </>
    );

    // ── EL QUINCENAL: TRES PASOS («Todo lo validado antes del 1 de septiembre») ──
    //
    // Era una sola pantalla con cuatro preguntas (T7 del 16-08) y pasa a tener la misma
    // forma que el mensual: 1 sus datos, 2 sus sensaciones y dudas, y el 3 la respuesta,
    // que se ve al mandarlo. La cabecera «Son 3 pasos» sale en los tres, con el suyo
    // marcado, y por eso desde el primero ya sabe que después de enviar pasa algo.
    //
    // El paso 1 es el que más cambia: antes se le PEDÍA el peso y ahora se le ENSEÑA su
    // semana entera -- lo que ha hecho, cómo se ha sentido y de qué dos días sale su peso
    // -- para que solo tenga que confirmarla.
    if (!esMensual) {
        const tituloQuincenal = TITULOS_EN_LA_CABECERA[ficha?.tipo] || 'Reporte';
        return (
            <div className="space-y-4">
                {/* EL QUINCENAL DEL ARTEFACTO (26-10) lleva en cada pantalla su cabecera con la
                    barra de tres tramos (`CabeceraDelQuincenal`), y no «Son 3 pasos». */}
                {!esQuincenal && (
                    <CabeceraDePasos paso={pasoMensual}
                        pasos={pasosDelRapido(ficha?.con_respuesta !== false)} titulo={tituloQuincenal} />
                )}

                {pasoMensual === 1 && (
                    <>
                        {/* EL SUBTÍTULO LO PONE EL PASO, no esta pantalla: en la versión
                            sin check-in dice otra cosa («Cinco preguntas y pasas al paso
                            2»), y tenerlo aquí escribía «Sale de tu check-in» encima de un
                            aviso que dice justo que no los tenemos. */}
                        {!esQuincenal && <RotuloDelPaso paso={1} rotulos={ROTULOS_DEL_QUINCENAL} />}
                        {esQuincenal ? (
                            <QuincenalResumen api={api} valores={valores} set={set}
                                onConfirmar={confirmarPaso1} />
                        ) : (
                        <QuincenalPaso1
                            api={api} valores={valores} set={set}
                            huecosRespuestas={valores.huecos_paso1}
                            onHueco={(tipo, v) => set('huecos_paso1',
                                { ...(valores.huecos_paso1 || {}), [tipo]: v })}
                            onConfirmar={confirmarPaso1} />
                        )}
                        {bloqueAplazar}
                    </>
                )}

                {pasoMensual === 2 && (
                    <>
                        {esQuincenal
                            ? <CabeceraDelQuincenal titulo="Ahora dame feedback" paso={2} />
                            : <RotuloDelPaso paso={2} rotulos={ROTULOS_DEL_QUINCENAL} />}
                        {esQuincenal
                            ? <QuincenalPreguntas preguntas={preguntasQuincenal} valores={valores} set={set} />
                            : <ReporteQuincenal valores={valores} set={set} bloques={bloques} />}
                        <div className="flex gap-2">
                            <button type="button" onClick={() => irAPaso(1)} data-testid="quincenal-atras"
                                className="rounded-xl px-4 py-3 text-sm font-bold border border-border bg-card text-foreground/70 hover:border-foreground/30 transition-colors">
                                Atrás
                            </button>
                            <button type="button" onClick={enviar} disabled={enviando}
                                data-testid="submit-report-btn"
                                className="flex-1 py-3 rounded-xl font-bold text-sm uppercase tracking-wider text-white flex items-center justify-center gap-2 transition-all disabled:opacity-40"
                                style={{ backgroundColor: ORANGE }}>
                                <Send className="w-4 h-4" />
                                {enviando ? 'Enviando...' : (esQuincenal ? 'Enviar' : 'Enviar reporte')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ── EL MENSUAL: UNA PANTALLA POR SECCIÓN (doc del 25-10, rediseño del 28-10) ──
    //
    // Arriba, el título de la sección en la ceja y las barras planas de avance del check-in
    // diario (un tramo por sección). Continuar solo con la sección entera, y en la última
    // (fotos y medidas) se envía. Qué secciones salen lo dice el servidor (`secciones_mensual`).
    //
    // «Tu peso» espera al resumen: de él sale si se salta (`peso_de_la_semana`).
    if (!seccion) return null;
    if (seccion.clave === 'peso' && !resumen && cargandoResumen) {
        return <div className="animate-pulse h-40 bg-card rounded-2xl" data-testid="mensual-cargando" />;
    }
    const primera = pesoDeLaSemana ? 2 : 1;
    const ultimaSeccion = pasoMensual >= secciones.length;
    const siguiente = async () => {
        if (seccion.clave === 'peso' || seccion.clave === 'mes'
            ? !await revisarElPeso() : !revisarLaSeccion(seccion)) return;
        irAPaso(Math.min(secciones.length, pasoMensual + 1));
    };
    // Al tocar una respuesta que cierra la sección («Mismo objetivo», el objetivo nuevo...):
    // con esos valores, si está entera, a la siguiente; si no, se queda.
    const siguienteCon = (nuevos) => {
        if (faltaEnLaSeccion(seccion, nuevos, grados)) return;
        irAPaso(Math.min(secciones.length, pasoMensual + 1));
    };
    // El peso que acaba de apuntar en «Tu peso» es el del reporte, con el día de hoy. Se vuelve
    // a pedir el resumen, que «Tu mes» lo cuenta.
    const pesoRegistrado = (p, pasar) => {
        setValores((v) => ({ ...v, weight: String(p.valor), peso_fecha: p.fecha || hoyLocal() }));
        pesoConfirmado.current = Number(p.valor);
        if (pasar) { cargarResumen(); irAPaso(2); }
    };
    const boton = 'w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider text-black flex items-center justify-center gap-2 disabled:opacity-40';
    // Con «Necesito reducir el número de suplementos» marca varios: ahí sí hace falta el botón.
    const r = valores.respuestas_mensual || {};
    const eligiendoVarios = (seccion.preguntas || []).some((p) => p.quitar && r[p.clave] === p.quitar.si);
    const conContinuar = !seccion.automatica || eligiendoVarios || enteraAlEntrar;
    const esTuMes = seccion.clave === 'mes';
    return (
        <div className="space-y-4" data-testid={`mensual-seccion-${seccion.numero}`}>
            <div className="space-y-1.5" data-testid="mensual-cabecera">
                {pasoMensual > primera && (
                    <button type="button" onClick={() => irAPaso(Math.max(primera, pasoMensual - 1))}
                        data-testid="mensual-atras"
                        className="text-[13px] text-muted-foreground hover:text-foreground">
                        ‹ Atrás
                    </button>
                )}
                <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}
                    data-testid="mensual-titulo-seccion">
                    {seccion.titulo}
                </p>
                {esTuMes && resumen?.titulo && (
                    <h2 className="text-2xl font-bold text-foreground uppercase leading-tight"
                        style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                        {resumen.titulo}
                    </h2>
                )}
                {esTuMes && resumen?.subtitulo && (
                    <p className="text-sm text-muted-foreground">{resumen.subtitulo}</p>
                )}
                <div className="pt-1.5">
                    <BarraDeTramos hechos={pasoMensual} de={secciones.length}
                        testid={`mensual-paso-${pasoMensual}-de-${secciones.length}`} />
                </div>
            </div>

            {seccion.clave === 'peso' && (
                <MensualTuPeso api={api} onRegistrado={pesoRegistrado}
                    ultimo={resumen?.peso?.ultimo
                        ? { valor: resumen.peso.ultimo.valor, fecha: resumen.peso.ultimo.fecha } : null} />
            )}

            {esTuMes && (
                <MensualTuMes resumen={resumen} cargando={cargandoResumen} fallo={falloResumen}
                    onReintentar={cargarResumen} />
            )}

            {seccion.clave === 'fotos' && (
                <MensualPaso3 api={api} token={token} valores={valores} set={set}
                    prev={prev} plazo={plazo} />
            )}

            {!['peso', 'mes', 'fotos'].includes(seccion.clave) && (
                <MensualSeccion api={api} seccion={seccion} valores={valores} set={set} grados={grados}
                    onSigue={siguienteCon} />
            )}

            {seccion.clave !== 'peso' && conContinuar && (ultimaSeccion ? (
                <button type="button" onClick={enviar} disabled={enviando}
                    data-testid="submit-report-btn" className={boton} style={{ backgroundColor: ORANGE }}>
                    <Send className="w-4 h-4" />
                    {enviando ? 'Enviando...' : 'Enviar'}
                </button>
            ) : (
                <button type="button" onClick={siguiente} data-testid="mensual-siguiente"
                    className={boton} style={{ backgroundColor: ORANGE }}>
                    Continuar
                </button>
            ))}

            {/* El aplazamiento, una vez y en la primera pantalla (Jesús, 20-10). */}
            {pasoMensual === primera && bloqueAplazar}
        </div>
    );
};

/**
 * Los huecos, como los entiende el servidor. Solo los contesta el quincenal: el mensual del
 * 25-10 ya no los pregunta (sus grados salen de los check-in o se le preguntan enteros).
 */
const huecosParaElServidor = (ficha, valores) => {
    if (ficha?.tipo === 'mensual' || !valores.entreno_huecos) return {};
    return { entrenamiento: valores.entreno_huecos === 'si_no_lo_apunte'
        ? 'si_pero_no_apunte' : 'no_lo_hice' };
};

export default FormularioReporte;
